import React, { useContext, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import moment from "moment";
import get from "lodash/get";

import { Box, Button, Container, Grid, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LogoutIcon from "@mui/icons-material/Logout";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";

import { auth } from '../config/firebase';
import { DataContext } from '../components/data/DataContext';
import { DataContextType } from '../components/data_types/interfaces';
import { DIVISION_BADGES } from '../components/data/const';
import { DIVISION_NAMES } from '../components/data/player_insights';
import { useLogout } from '../components/buttons/Logout';
import Loading from '../components/layout/Loading';
import TopBar from '../components/layout/TopBar';
import { gridSpacing } from '../assets/theme/muitheme';
import { DS, DeltaPill, Overline, PageHeader, Panel, enterSx, numericSx } from '../components/shared/Dashboard';

const PROVIDER_LABELS: { [providerId: string]: string } = {
    'google.com': 'Google',
    'apple.com': 'Apple',
    'password': 'Email',
};

const initialsOf = (name: string) =>
    (name || '?')
        .split(/[\s_]+/)
        .filter(Boolean)
        .slice(0, 2)
        .map(part => part[0].toUpperCase())
        .join('');

const RolePill: React.FC<{ color: string; children: React.ReactNode }> = ({ color, children }) => (
    <Box component="span" sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1.25,
        py: '2px',
        borderRadius: '999px',
        background: alpha(color, 0.14),
        color,
        fontSize: '12px',
        fontWeight: 700,
        whiteSpace: 'nowrap',
    }}>
        {children}
    </Box>
);

const Stat: React.FC<{ label: string; value: React.ReactNode; sub?: React.ReactNode }> = ({ label, value, sub }) => (
    <Box sx={{ minWidth: 0 }}>
        <Overline>{label}</Overline>
        <Typography component="div" sx={{ fontSize: '22px', fontWeight: 700, color: DS.text, lineHeight: 1.3, mt: 0.25, ...numericSx }}>
            {value}
        </Typography>
        {sub && (
            <Typography component="div" sx={{ fontSize: '12px', color: DS.textMuted, mt: 0.25, ...numericSx }}>
                {sub}
            </Typography>
        )}
    </Box>
);

const DetailRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 1, borderBottom: `1px solid ${DS.border}`, '&:last-of-type': { borderBottom: 'none' } }}>
        <Typography sx={{ fontSize: '13px', color: DS.textMuted, flexShrink: 0 }}>{label}</Typography>
        <Typography component="div" sx={{ fontSize: '13px', color: DS.text, fontWeight: 600, textAlign: 'right', minWidth: 0, wordBreak: 'break-word' }}>
            {children}
        </Typography>
    </Box>
);

const formatAuthTime = (value?: string) => value ? moment(value).format('MMM D, YYYY') : '—';

const UserProfile = () => {
    const [user, loading, error] = useAuthState(auth);
    const navigate = useNavigate();
    const { logout, disabled: loggingOut } = useLogout();

    const context = useContext<DataContextType | undefined>(DataContext);
    const { playerData, playerSeasonData, previousSeasonRating, isClubMember, isAdmin, isCreator } = context || {};

    const [copied, setCopied] = useState(false);

    if (loading) return <Loading />;
    if (error) {
        return (
            <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
                <Typography sx={{ fontWeight: 700, color: DS.text }}>Could not load your profile</Typography>
                <Typography sx={{ fontSize: '13px', color: DS.textMuted, mt: 0.5 }}>{error.message}</Typography>
            </Container>
        );
    }
    if (!user) return <Navigate to="/login" replace />;

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(user.uid);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch (copyError) {
            console.error('Could not copy player ID:', copyError);
        }
    };

    // The in-game name is what other players and organizers know you by
    const gameName: string | undefined = get(playerData, 'playerSettings.displayName');
    const name = gameName || user.displayName || 'Player';
    const providerId = user.providerData[0]?.providerId;
    const providerLabel = providerId ? PROVIDER_LABELS[providerId] ?? providerId : '—';

    const division: number | undefined = playerSeasonData?.division;
    const hasDivision = typeof division === 'number' && division >= 0 && division < DIVISION_NAMES.length;
    const rating: number | undefined = playerSeasonData?.rating;
    const ratingChange = typeof rating === 'number' && typeof previousSeasonRating === 'number'
        ? rating - previousSeasonRating
        : undefined;

    return (
        <Container maxWidth="lg">
            <Box sx={{ flexGrow: 1 }}>
                <TopBar />
            </Box>

            <Grid container spacing={gridSpacing} sx={{ mb: 6 }}>
                <PageHeader
                    overline="Account"
                    title="Your profile"
                    subtitle="Your player identity, season standing and sign-in details."
                    action={
                        <Button
                            startIcon={<ArrowBackIcon />}
                            onClick={() => navigate('/')}
                            sx={{ borderRadius: '20px', textTransform: 'none', fontWeight: 700, color: DS.textMuted }}
                        >
                            Back to metrics
                        </Button>
                    }
                />

                {/* Identity */}
                <Grid item xs={12}>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: { xs: 2, sm: 3 },
                        flexWrap: 'wrap',
                        p: { xs: 2, sm: 3 },
                        borderRadius: DS.radius,
                        border: `1px solid ${DS.border}`,
                        background: `radial-gradient(120% 140% at 100% 0%, ${alpha(DS.brand, 0.14)} 0%, transparent 55%), ${DS.surface}`,
                        ...enterSx(0.05),
                    }}>
                        <Box sx={{
                            width: { xs: 64, sm: 80 },
                            height: { xs: 64, sm: 80 },
                            flexShrink: 0,
                            borderRadius: '50%',
                            p: '3px',
                            background: `linear-gradient(135deg, ${DS.brand}, ${alpha(DS.brand, 0.2)})`,
                        }}>
                            <Box sx={{
                                width: '100%',
                                height: '100%',
                                borderRadius: '50%',
                                overflow: 'hidden',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: DS.surface,
                                color: DS.brand,
                                fontWeight: 800,
                                fontSize: { xs: '22px', sm: '26px' },
                            }}>
                                {user.photoURL
                                    ? <Box component="img" src={user.photoURL} alt="" referrerPolicy="no-referrer" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    : initialsOf(name)}
                            </Box>
                        </Box>

                        <Box sx={{ flex: 1, minWidth: 200 }}>
                            <Typography sx={{ fontSize: { xs: '22px', sm: '28px' }, fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.2, color: DS.text, wordBreak: 'break-word' }}>
                                {name}
                            </Typography>
                            {gameName && user.displayName && gameName !== user.displayName && (
                                <Typography sx={{ fontSize: '13px', color: DS.textMuted, mt: 0.25 }}>
                                    Signed in as {user.displayName}
                                </Typography>
                            )}
                            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1 }}>
                                {hasDivision && (
                                    <RolePill color={DS.gold}>
                                        <Box component="img" src={DIVISION_BADGES[division!]} alt="" sx={{ width: 14, height: 14 }} />
                                        {DIVISION_NAMES[division!]} Division
                                    </RolePill>
                                )}
                                {isClubMember && <RolePill color={DS.brand}>DGV Club member</RolePill>}
                                {isAdmin && <RolePill color={DS.violet}>Admin</RolePill>}
                                {!isAdmin && isCreator && <RolePill color={DS.info}>Event creator</RolePill>}
                            </Box>
                        </Box>

                        {typeof rating === 'number' && (
                            <Box sx={{ textAlign: { xs: 'left', sm: 'right' }, flexShrink: 0 }}>
                                <Overline>Season rating</Overline>
                                <Typography sx={{ fontSize: { xs: '32px', sm: '40px' }, fontWeight: 800, lineHeight: 1.1, letterSpacing: '-0.02em', color: DS.text, ...numericSx }}>
                                    {Math.round(rating)}
                                </Typography>
                                {typeof ratingChange === 'number' && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, justifyContent: { xs: 'flex-start', sm: 'flex-end' }, mt: 0.5 }}>
                                        <DeltaPill value={Math.round(ratingChange)} />
                                        <Typography sx={{ fontSize: '12px', color: DS.textMuted }}>vs last season</Typography>
                                    </Box>
                                )}
                            </Box>
                        )}
                    </Box>
                </Grid>

                {/* Player ID */}
                <Panel
                    title="Player ID"
                    subtitle="Your unique ID in Disc Golf Valley."
                    grid={{ xs: 12, md: 7 }}
                    delay={0.1}
                >
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        flexWrap: 'wrap',
                        p: 1.5,
                        borderRadius: '12px',
                        background: DS.inset,
                        border: `1px solid ${DS.border}`,
                    }}>
                        <Typography sx={{
                            flex: '1 1 220px',
                            minWidth: 0,
                            fontFamily: 'monospace',
                            fontSize: '15px',
                            fontWeight: 600,
                            color: DS.text,
                            wordBreak: 'break-all',
                            userSelect: 'all',
                        }}>
                            {user.uid}
                        </Typography>
                        <Button
                            variant={copied ? 'outlined' : 'contained'}
                            color={copied ? 'success' : 'primary'}
                            onClick={handleCopy}
                            startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
                            sx={{ flexShrink: 0, borderRadius: '20px', textTransform: 'none', fontWeight: 700, minWidth: 110 }}
                        >
                            {copied ? 'Copied' : 'Copy ID'}
                        </Button>
                    </Box>
                </Panel>

                {/* Season */}
                <Panel title="This season" subtitle="Your ranked standing" grid={{ xs: 12, md: 5 }} delay={0.15}>
                    {playerSeasonData ? (
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 2.5 }}>
                            <Stat label="Rating" value={typeof rating === 'number' ? Math.round(rating) : '—'} />
                            <Stat label="Division" value={hasDivision ? DIVISION_NAMES[division!] : '—'} />
                            <Stat label="Matches" value={playerSeasonData.gamesCount ?? '—'} />
                            <Stat
                                label="Last season"
                                value={typeof previousSeasonRating === 'number' ? Math.round(previousSeasonRating) : '—'}
                                sub={typeof previousSeasonRating === 'number' ? 'Final rating' : 'No rating'}
                            />
                        </Box>
                    ) : (
                        <Box sx={{ textAlign: 'center', py: 3 }}>
                            <EmojiEventsIcon sx={{ fontSize: 32, color: DS.textFaint, mb: 1 }} />
                            <Typography sx={{ fontWeight: 700, color: DS.text }}>No ranked matches yet</Typography>
                            <Typography sx={{ fontSize: '13px', color: DS.textMuted, mt: 0.5 }}>
                                Play a ranked match this season to see your standing here.
                            </Typography>
                        </Box>
                    )}
                </Panel>

                {/* Account */}
                <Panel title="Account" grid={{ xs: 12 }} delay={0.2}>
                    <Box sx={{ display: 'flex', gap: { xs: 2, md: 4 }, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                        <Box sx={{ flex: '1 1 320px', minWidth: 0 }}>
                            <DetailRow label="Email">{user.email || '—'}</DetailRow>
                            <DetailRow label="Signed in with">{providerLabel}</DetailRow>
                            <DetailRow label="Account created">{formatAuthTime(user.metadata.creationTime)}</DetailRow>
                            <DetailRow label="Last sign-in">{formatAuthTime(user.metadata.lastSignInTime)}</DetailRow>
                        </Box>
                        <Button
                            variant="outlined"
                            color="error"
                            onClick={logout}
                            disabled={loggingOut}
                            startIcon={<LogoutIcon />}
                            sx={{ borderRadius: '20px', textTransform: 'none', fontWeight: 700, px: 2.5 }}
                        >
                            Sign out
                        </Button>
                    </Box>
                </Panel>
            </Grid>
        </Container>
    );
};

export default UserProfile;
